import { PhaseProjectEditor } from "./PhaseProjectEditor";
import {
  Button,
  GlobalNotAuthorized,
  LoadingSpinner,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import {
  phaseActionDefinitionsCatalogQueryOptions,
  phaseProjectQueryOptions,
  reusablePhasesCatalogQueryOptions,
} from "@/lib/data/react-query/matters/matter-phase";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { MATTER_PHASE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

export function PhaseProjectEditorPage({
  projectId,
  startEditing,
}: {
  projectId?: string;
  startEditing?: boolean;
}) {
  const { hasGrant } = useUser();
  const navigate = useNavigate({ from: "/matters/settings/phase-projects" });
  const canCreate = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.phaseProject,
    "create",
  );
  const canUpdate = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.phaseProject,
    "update",
  );
  const canCreateReusablePhase = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.reusablePhase,
    "create",
  );
  const projectQuery = useQuery(
    phaseProjectQueryOptions(projectId ?? "", Boolean(projectId)),
  );
  const settingsQuery = useQuery(retrieveMatterSettingsQueryOptions());
  const phasesQuery = useQuery(reusablePhasesCatalogQueryOptions());
  const actionsQuery = useQuery(phaseActionDefinitionsCatalogQueryOptions());

  if (!projectId && !canCreate) return <GlobalNotAuthorized />;
  if (
    (projectId && projectQuery.isPending) ||
    settingsQuery.isPending ||
    phasesQuery.isPending ||
    actionsQuery.isPending
  ) {
    return (
      <div className="flex min-h-80 items-center justify-center">
        <LoadingSpinner className="size-6" />
      </div>
    );
  }
  if (
    (projectId && projectQuery.isError) ||
    settingsQuery.isError ||
    phasesQuery.isError ||
    actionsQuery.isError
  ) {
    return (
      <div className="mx-auto w-full max-w-7xl space-y-4">
        <Button
          type="button"
          variant="ghost"
          className="-ml-3 gap-2"
          onClick={() =>
            void navigate({
              to: "/matters/settings/phase-projects",
              search: (previous) => ({ ...previous, editor: undefined }),
              replace: true,
            })
          }
        >
          <ArrowLeft className="size-4" /> Phase projects
        </Button>
        <Text className="text-destructive">
          Could not load the phase project editor.
        </Text>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            void Promise.all([
              ...(projectId ? [projectQuery.refetch()] : []),
              settingsQuery.refetch(),
              phasesQuery.refetch(),
              actionsQuery.refetch(),
            ]);
          }}
        >
          Try again
        </Button>
      </div>
    );
  }

  const project = projectQuery.data?.data;
  const incidentTypes = (settingsQuery.data.data.incidentTypes ?? []).map(
    (type) => ({
      id: type.id,
      label: type.label ?? "Unnamed Incident Type",
    }),
  );
  return (
    <PhaseProjectEditor
      key={projectId ?? "new"}
      project={project}
      startEditing={startEditing}
      incidentTypes={incidentTypes}
      claimTypes={settingsQuery.data.data.claimTypes ?? []}
      reusablePhases={phasesQuery.data.data}
      actionDefinitions={actionsQuery.data.data}
      readOnly={Boolean(
        project ? project.lifecycle === "Archived" || !canUpdate : !canCreate,
      )}
      canCreateReusablePhase={canCreateReusablePhase}
      canCreateAction={canCreate}
    />
  );
}
